import React, { useEffect, useRef, useState } from 'react';
import { PmsConnection, connectHostex, getHostexConnection } from '../../api.channelManager';
import { ApiKeyArt, ChooseProviderArt, DoneArt, TestReservationArt, WebhookArt } from './StepArt';
import './channelManager.css';

const STEP_LABELS = ['Provider', 'API key', 'Webhook', 'Test reservation', 'Done'];

/** Gives the manager time to actually go create the reservation in Hostex before the first check. */
const FIRST_POLL_DELAY_MS = 20000;
/** How often to re-check afterwards whether Hostex's first webhook (and its secret) has arrived. */
const POLL_MS = 6000;
/** After this many automatic checks, stop polling — only the manual button keeps working. */
const MAX_POLLS = 50;

interface Props {
  onCancel: () => void;
  onDone: (conn: PmsConnection) => void;
}

const SetupWizard: React.FC<Props> = ({ onCancel, onDone }) => {
  const [step, setStep] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [conn, setConn] = useState<PmsConnection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [nextPollAt, setNextPollAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef = useRef(0);

  const clearPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  useEffect(() => clearPolling, []);

  // Ticks once a second only while a countdown to the next check is actually running.
  useEffect(() => {
    if (nextPollAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [nextPollAt]);

  const scheduleNextPoll = (delayMs: number) => {
    clearPolling();
    setNextPollAt(Date.now() + delayMs);
    pollTimeoutRef.current = setTimeout(async () => {
      pollCountRef.current += 1;
      setNextPollAt(null);
      setBusy(true);
      try {
        const next = await getHostexConnection();
        setConn(next);
        if (next.hasWebhookSecret) {
          setBusy(false);
          return;
        }
      } catch {
        // Transient errors are not worth surfacing during a background poll.
      }
      setBusy(false);
      if (pollCountRef.current >= MAX_POLLS) {
        setPollExhausted(true);
        return;
      }
      scheduleNextPoll(POLL_MS);
    }, delayMs);
  };

  const startPolling = () => {
    pollCountRef.current = 0;
    setPollExhausted(false);
    scheduleNextPoll(FIRST_POLL_DELAY_MS);
  };

  const remainingSeconds = nextPollAt != null ? Math.max(0, Math.ceil((nextPollAt - now) / 1000)) : 0;
  const countingDown = !pollExhausted && nextPollAt != null && remainingSeconds > 0;

  const handleConnect = async () => {
    if (!apiKey.trim()) return;
    setError(null);
    setBusy(true);
    try {
      const next = await connectHostex(apiKey.trim());
      setConn(next);
      if (next.canWrite) {
        setStep(2);
      } else {
        setError(
          'This key is read-only — Hostex could not register the webhook with it. Create a key with writable scope and connect again.'
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Could not connect');
    } finally {
      setBusy(false);
    }
  };

  const handleWebhookContinue = () => {
    setStep(3);
    startPolling();
  };

  const handleManualCheck = async () => {
    setBusy(true);
    try {
      const next = await getHostexConnection();
      setConn(next);
    } finally {
      setBusy(false);
    }
  };

  const copyWebhookUrl = () => {
    if (!conn?.webhookUrl) return;
    navigator.clipboard.writeText(conn.webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const testArrived = !!conn?.hasWebhookSecret;

  return (
    <div className="cm-wizard">
      <div className="cm-stepper" aria-hidden="true">
        {STEP_LABELS.map((_, i) => (
          <div key={i} className={`cm-step-dot ${i < step ? 'done' : i === step ? 'active' : ''}`} />
        ))}
      </div>
      <div className="cm-step-label">
        Step {step + 1} of {STEP_LABELS.length} · {STEP_LABELS[step]}
      </div>

      <div className="cm-wizard-body">
        <div className="cm-wizard-art">
          {step === 0 && <ChooseProviderArt />}
          {step === 1 && <ApiKeyArt />}
          {step === 2 && <WebhookArt />}
          {step === 3 && <TestReservationArt />}
          {step === 4 && <DoneArt />}
        </div>

        <div className="cm-wizard-content">
          {step === 0 && (
            <>
              <h3>Choose a channel manager</h3>
              <p>Guestbook currently connects to Hostex. More providers are on the way.</p>
              <button type="button" className="app-btn app-btn-green app-btn-standard" onClick={() => setStep(1)}>
                Continue with Hostex
              </button>
            </>
          )}

          {step === 1 && (
            <>
              <h3>Create an API key</h3>
              <p>
                In Hostex, go to <strong>Workspace → Open API → API</strong>, create a key with{' '}
                <strong>writable</strong> scope, then paste it here.
              </p>
              {error && <div style={{ color: '#c53030', fontSize: '0.85rem', marginBottom: 10 }}>{error}</div>}
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Hostex-Access-Token"
                style={{
                  width: '100%',
                  maxWidth: 380,
                  padding: '9px 12px',
                  border: '1px solid var(--border-input)',
                  borderRadius: 8,
                  fontSize: '0.9rem',
                  marginBottom: 14,
                  display: 'block',
                }}
              />
              <button
                type="button"
                className="app-btn app-btn-green app-btn-standard"
                disabled={busy || !apiKey.trim()}
                onClick={handleConnect}
              >
                {busy ? 'Connecting…' : 'Connect'}
              </button>
            </>
          )}

          {step === 2 && conn && (
            <>
              <h3>Webhook</h3>
              {conn.lastError ? (
                <>
                  <div className="cm-waiting-row" style={{ color: '#991b1b', background: '#fef2f2' }}>
                    ⚠️ Couldn't register it automatically: {conn.lastError}
                  </div>
                  <p>
                    Add it by hand instead — in Hostex, open <strong>Workspace → Open API → Webhook</strong>, add a
                    new webhook, and paste this URL in.
                  </p>
                  <div className="cm-webhook-box">
                    <code title={conn.webhookUrl || ''}>{conn.webhookUrl}</code>
                    <button
                      type="button"
                      className="app-btn app-btn-blue app-btn-compact"
                      onClick={copyWebhookUrl}
                      style={{ flexShrink: 0 }}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="cm-waiting-row" style={{ color: '#166534', background: '#f0fdf4' }}>
                  ✅ Registered automatically — Hostex is set up to send Guestbook reservation events.
                </div>
              )}
              <button type="button" className="app-btn app-btn-green app-btn-standard" onClick={handleWebhookContinue}>
                Continue
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h3>Create a test reservation</h3>
              <p>
                In Hostex, manually create a test reservation for one property — any dates, any guest name.
                Guestbook needs to receive one real event from Hostex to finish verifying the connection.
              </p>
              {!testArrived ? (
                <div className="cm-waiting-row">
                  {!pollExhausted && <span className="cm-spinner" />}
                  {pollExhausted
                    ? "We stopped checking automatically — click below whenever you've made the reservation."
                    : countingDown
                    ? `Waiting for the test reservation to arrive… next check in ${remainingSeconds}s`
                    : 'Checking…'}
                </div>
              ) : (
                <div className="cm-waiting-row" style={{ color: '#166534' }}>
                  ✅ Received — the connection is verified.
                </div>
              )}
              {!testArrived && (
                <button
                  type="button"
                  className="app-btn app-btn-blue app-btn-standard"
                  disabled={busy || countingDown}
                  onClick={handleManualCheck}
                >
                  {busy
                    ? 'Checking…'
                    : countingDown
                    ? `Available in ${remainingSeconds}s`
                    : 'I made the reservation — check now'}
                </button>
              )}
              {testArrived && (
                <button type="button" className="app-btn app-btn-green app-btn-standard" onClick={() => setStep(4)}>
                  Continue
                </button>
              )}
            </>
          )}

          {step === 4 && conn && (
            <>
              <h3>Hostex is connected</h3>
              <p>You can now map apartments to Hostex properties and customize the guest messages Hostex sends.</p>
              <button type="button" className="app-btn app-btn-green app-btn-standard" onClick={() => onDone(conn)}>
                Go to channel managers
              </button>
            </>
          )}
        </div>
      </div>

      <div className="cm-wizard-footer">
        <button type="button" className="app-btn app-btn-blue app-btn-compact" onClick={onCancel}>
          Cancel
        </button>
        {step > 0 && step < 4 && (
          <div className="cm-wizard-footer-actions">
            <button
              type="button"
              className="app-btn app-btn-blue app-btn-compact"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetupWizard;
